import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with default headers
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and business_id
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Note: business_id is now passed directly to specific API calls, not through interceptor
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('sessionToken');
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);

// Flag a query as a bug report
export const flagQuery = async (bugData) => {
  try {
    const response = await apiClient.post('/api/bug/flag', bugData);
    return response.data;
  } catch (error) {
    console.error('Error flagging query:', error);
    throw error;
  }
};

// Get bug reports list with filtering and pagination
export const getBugReports = async (params = {}, businessId = null) => {
  try {
    console.log('🌐 [bugAPI] getBugReports called with params:', params);
    console.log('🌐 [bugAPI] Business ID passed:', businessId);
    
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const url = `/api/bug/list?${queryParams.toString()}`;
    console.log('🌐 [bugAPI] Making request to:', url);
    
    // Use apiClient which has auth interceptor
    const config = {};
    
    // Add business_id header if provided
    if (businessId) {
      config.headers = {
        'x-business-id': businessId
      };
      console.log('✅ [bugAPI] Added x-business-id header:', businessId);
    }
    
    const response = await apiClient.get(url, config);
    console.log('🌐 [bugAPI] Response received:', response.data);
    return response;
  } catch (error) {
    console.error('❌ [bugAPI] Error fetching bug reports:', error);
    console.error('❌ [bugAPI] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url
    });
    throw error;
  }
};

// Get specific bug report by ID
export const getBugReport = async (id) => {
  try {
    const response = await apiClient.get(`/api/bug/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching bug report:', error);
    throw error;
  }
};

// Update bug report status
export const updateBugStatus = async (id, status) => {
  try {
    const response = await apiClient.put(`/api/bug/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating bug status:', error);
    throw error;
  }
};

// Get bug reports by category (analytics)
export const getBugReportsByCategory = async () => {
  try {
    const response = await apiClient.get('/api/bug/analytics/by-category');
    return response.data;
  } catch (error) {
    console.error('Error fetching bug reports by category:', error);
    throw error;
  }
};

// Get bug reports by status (analytics)
export const getBugReportsByStatus = async () => {
  try {
    const response = await apiClient.get('/api/bug/analytics/by-status');
    return response.data;
  } catch (error) {
    console.error('Error fetching bug reports by status:', error);
    throw error;
  }
};

export default {
  flagQuery,
  getBugReports,
  getBugReport,
  updateBugStatus,
  getBugReportsByCategory,
  getBugReportsByStatus
};
